import { NavLink } from "react-router-dom";
import {
  LayoutDashboard,
  ShieldCheck,
  Users,
  FolderKanban,
  TrendingUp,
  Wrench,
  Package,
  Wallet,
} from "lucide-react";
import { cn } from "../lib/utils";
import type { User } from "../types/api";
import { useTranslation } from "react-i18next";

const navItems = [
  { to: "/admin", labelKey: "admin.sidebarDashboard", icon: LayoutDashboard },
  { to: "/admin/users", labelKey: "admin.sidebarUsers", icon: Users, managerCanSee: true },
  {
    to: "/admin/projects",
    labelKey: "admin.sidebarProjects",
    icon: FolderKanban,
  },
  {
    to: "/admin/investment-packs",
    labelKey: "admin.sidebarInvestmentPacks",
    icon: Package,
  },
  { to: "/admin/roles", labelKey: "admin.sidebarRoles", icon: ShieldCheck },
  {
    to: "/admin/simulated-trades",
    labelKey: "admin.sidebarSimulatedTrades",
    icon: TrendingUp,
    permission: "trades.simulate",
    managerCanSee: true,
  },
  {
    to: "/admin/maintenance",
    labelKey: "admin.sidebarMaintenance",
    icon: Wrench,
    managerCanSee: true,
  },
  {
    to: "/admin/crypto-wallets",
    labelKey: "admin.sidebarCryptoWallets",
    icon: Wallet,
  },
  {
    to: "/admin/superadmin/client-investments",
    labelKey: "admin.sidebarSuperadminInvestments",
    icon: TrendingUp,
    superadminOnly: true,
  },
];

interface AdminSidebarProps {
  user?: User | null;
  className?: string;
  onNavigate?: () => void;
}

export const AdminSidebar = ({ user, className, onNavigate }: AdminSidebarProps) => {
  const { t } = useTranslation();
  return (
    <aside
      className={cn(
        "flex w-full max-w-xs flex-col rounded-3xl border border-slate-200 bg-white p-6 shadow-xl dark:border-white/10 dark:bg-white/5 dark:shadow-2xl",
        className
      )}>
      <div className="rounded-2xl bg-gradient-to-br from-cyan-100/80 to-blue-100/60 p-4 text-sm text-slate-800 dark:from-cyan-500/10 dark:to-blue-500/20 dark:text-white/80">
        <p className="text-xs uppercase tracking-wide text-slate-600 dark:text-white/70">
          {t("admin.sidebarAdmin")}
        </p>
        <p className="mt-1 text-lg font-semibold text-slate-900 dark:text-white">
          {user
            ? `${user.firstName} ${user.lastName}`
            : t("admin.sidebarDefaultUser")}
        </p>
        <p className="text-xs text-slate-600 dark:text-white/60">
          {t("admin.sidebarRoleLabel")} {user?.role ?? "admin"}
        </p>
      </div>
      <nav className="mt-6 space-y-1">
        {navItems
          .filter((item) => {
            const isAdmin = user?.role === "admin";
            const isManager =
              user?.role === "manager_contact" ||
              user?.role === "manager_trader";
            const isSuperadmin = user?.role === "superadmin";
            if (item.superadminOnly) {
              return isSuperadmin;
            }
            if (isSuperadmin) {
              return Boolean(item.superadminOnly);
            }
            if (isAdmin) return true;
            if (isManager) {
              // Managers ne voient que : Utilisateurs, Trades simulés (si permission), Maintenance
              if (item.managerCanSee) {
                if (item.permission) {
                  return user?.permissions?.includes(item.permission);
                }
                return true;
              }
              return false;
            }
            return false;
          })
          .map(({ to, labelKey, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              onClick={onNavigate}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 rounded-2xl px-4 py-3 text-sm font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/40",
                  isActive
                    ? "bg-slate-900 text-white shadow-lg dark:bg-white/15"
                    : "text-slate-800 hover:bg-slate-100 hover:text-slate-950 dark:text-white/70 dark:hover:bg-white/10 dark:hover:text-white"
                )
              }>
              <Icon className="h-4 w-4" />
              {t(labelKey)}
            </NavLink>
          ))}
      </nav>
      <div className="mt-auto rounded-2xl border border-slate-200 bg-slate-50 p-4 text-xs text-slate-700 dark:border-white/10 dark:bg-black/20 dark:text-white/70">
        <p>{t("admin.sidebarDescription")}</p>
      </div>
    </aside>
  );
};
